import { Input, FormLabel, Flex, Box, Button } from '@chakra-ui/react';
import { useRef, type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '$/components/common/Icon';

type Error = 'format' | 'size';

interface Props {
  selectedFile: File | null;
  setSelectedFile: (file: File | null) => void;
  maxSizeInMb?: number;
  placeholder?: string;
  allowedExtensions?: string[];
  onError?: (error: Error | null) => void;
}

export const FileInput: FC<Props> = ({
  selectedFile,
  maxSizeInMb,
  setSelectedFile,
  placeholder,
  allowedExtensions = ['jpg', 'jpeg', 'png'],
  onError = () => {},
}) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<Error | null>(null);

  const handleFileChange = (file: File | undefined) => {
    if (!file) return;

    const extension = file.name.split('.').pop()?.toLowerCase();

    if (!extension || !allowedExtensions.includes(extension)) {
      onError('format');
      setError('format');
      setSelectedFile(null);
      return;
    }

    if (maxSizeInMb && file.size > maxSizeInMb * 1024 * 1024) {
      setSelectedFile(null);
      onError('size');
      setError('size');
      return;
    }

    setError(null);
    onError(null);
    setSelectedFile(file);
  };

  const border = () => {
    if (error != null) return '2px solid';
    if (selectedFile == null) return '1px solid';
    return '2px solid';
  };

  const borderColor = () => {
    if (error != null) return 'form.errorBorder';
    if (selectedFile == null) return 'border';
    return 'success.950';
  };

  return (
    <FormLabel w='full'>
      <Input
        ref={inputRef}
        display='none'
        w='full'
        p='0'
        accept={allowedExtensions.map((extension) => `.${extension}`).join(',')}
        id='file_input'
        onChange={(e) => handleFileChange(e.target.files?.[0])}
        placeholder={placeholder ?? t('editor.choose_picture')}
        type='file'
      />
      <Flex
        align='center'
        justify='space-between'
        w='full'
        p='1'
        pl='4'
        bg='bodyBackground'
        border={border()}
        borderColor={borderColor()}
        borderRadius='4px'
      >
        <Box
          as='span'
          overflowX='auto'
          mr='2'
          color={selectedFile?.name ? 'text' : 'placeholderText'}
        >
          {selectedFile?.name ?? placeholder ?? t('editor.choose_picture')}
        </Box>
        {selectedFile == null ? (
          <Button
            h='8'
            minH='0'
            fontSize='xs'
            fontWeight='normal'
            onClick={() => inputRef.current?.click()}
          >
            {t('editor.uploadIndicator')}
          </Button>
        ) : (
          <Button
            minW='4.625rem'
            h='8'
            minH='0'
            bg='success.950'
            _hover={{ bg: 'success.950' }}
            _active={{ bg: 'success.950' }}
            onClick={() => inputRef.current?.click()}
          >
            <Icon icon='check_icon' width='14px' />
          </Button>
        )}
      </Flex>
    </FormLabel>
  );
};
